import { useEffect, useMemo, useCallback, useState } from "react";
import {
  ActionButton,
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogTrigger,
  Divider,
  Flex,
  Heading,
  Text,
  View,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/storeConfig";
import {
  CallAccept,
  CallReject,
  CallRinging,
  EnvelopePlusIcon,
  NewIncomingChat,
} from "../Icons";
import {
  API_CALLBACK_REJECT,
  API_CHAT_REJECT,
  API_CASE_REJECT,
  CASE_STATES,
  CHAT_STATES,
} from "../../../constants/constants";
import {
  formatLabel,
  b64DecodeUnicode,
  getFormattedPhoneNumber,
} from "../../../utils/utils";
import { getInitialsAndColor } from "../../../utils";
import { useCustomerAttributes } from "../../../hooks/useCustomerAttributes";
import Emitter from "../../../emitter";
import { caseAnswer } from "../../../controller/cases";
import { IndividualChat } from "../../../types/chat.type";
import {
  API_CALLBACK_UPDATE,
  API_CHAT_UPDATE,
} from "../../../constants/events";
import { IndividualCase } from "../../../types/case.type";
import { SpeedDialData } from "../../../types/generic.types";

type Props = {
  type: "callback" | "voicemail" | "chat" | "case";
};

const AcceptDialog = ({ type }: Props) => {
  const agent = useSelector((state: RootState) => state.agent);
  const callbackOrVoicemail = useSelector((state: RootState) =>
    type === "callback" ? state.callback : state.voicemail
  );
  const chat = useSelector((state: RootState) => state.chat);
  const cases = useSelector((state: RootState) => state.cases);
  const speedDials = useSelector(
    (state: RootState) => state.history.speedDials
  );

  const [isOpen, setIsOpen] = useState(false);

  const incomingChat: IndividualChat | null = useMemo(() => {
    if (type !== "chat") return null;
    return (
      chat?.chats.find(
        (c: IndividualChat) => c.self?.state === CHAT_STATES.RINGING
      ) || null
    );
  }, [type, chat?.chats]);

  const incomingCase = useMemo(() => {
    if (type !== "case") return null;
    return (
      cases.cases.find(
        (c: IndividualCase) => c.self?.state === CASE_STATES.RINGING
      ) ?? null
    );
  }, [cases.cases, type]);

  const data =
    type === "chat"
      ? incomingChat
      : type === "case"
      ? incomingCase
      : callbackOrVoicemail;

  const customerAttrs = data?.customer?.attributes ?? {};
  const businessAttrs = agent?.businessAttributes ?? [];

  const { groupedAttributes } = useCustomerAttributes({
    customerAttrs,
    businessAttrs,
    type,
  });

  const { initials, avatarColor } = useMemo(() => {
    // Get phone number for speed dial lookup
    const phoneNumber =
      customerAttrs?.PHONE_NUMBER ||
      customerAttrs?.CustomerANI ||
      customerAttrs?.Customer_ANI ||
      "";
    const formattedCleanNumber = getFormattedPhoneNumber(phoneNumber);

    // First check speed dial entries for this phone number (PRIORITY)
    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === formattedCleanNumber
    );

    let firstName = "";
    let lastName = "";

    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      firstName = speedDialEntry.firstName;
      lastName = speedDialEntry.lastName;
    } else {
      firstName = customerAttrs.FIRSTNAME || "";
      lastName = customerAttrs.LASTNAME || "";
    }

    const first = firstName?.[0] ?? "";
    const last = lastName?.[0] ?? "";
    const fullInitials = (first + last).trim() || "NA";

    return {
      initials: fullInitials,
      avatarColor: getInitialsAndColor(fullInitials, "color"),
    };
  }, [customerAttrs, speedDials]);

  const fullName = useMemo(() => {
    // Get phone number for speed dial lookup
    const phoneNumber =
      customerAttrs?.PHONE_NUMBER ||
      customerAttrs?.CustomerANI ||
      customerAttrs?.Customer_ANI ||
      "";
    const formattedCleanNumber = getFormattedPhoneNumber(phoneNumber);

    // First check speed dial entries for this phone number (PRIORITY)
    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === formattedCleanNumber
    );

    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      return `${speedDialEntry.firstName} ${speedDialEntry.lastName}`;
    }

    // Fall back to customer attributes if speed dial doesn't have names
    return customerAttrs.FIRSTNAME && customerAttrs.LASTNAME
      ? `${customerAttrs.FIRSTNAME} ${customerAttrs.LASTNAME}`
      : "-";
  }, [customerAttrs, speedDials]);

  const emailOrId =
    type === "chat"
      ? customerAttrs.email ?? "-"
      : customerAttrs.CUSTOMER_ADOBE_ID ?? customerAttrs.EMAIL ?? "-";

  const headingIcon =
    type === "chat" ? (
      <NewIncomingChat />
    ) : type === "case" ? (
      <EnvelopePlusIcon />
    ) : (
      <CallRinging />
    );
  const headingText = `New incoming ${type}`;

  const rejectHandler = useCallback(() => {
    if (!data?.id) return;
    if (type === "chat") {
      Emitter.emit(API_CHAT_REJECT, data.id);
    } else if (type === "case") {
      Emitter.emit(API_CASE_REJECT, {
        body: {
          participantId: data.self?.id,
          conversationId: data.id,
          state: "disconnected",
        },
      });
    } else {
      Emitter.emit(API_CALLBACK_REJECT, {
        body: {
          participantId: data.self?.id,
          conversationId: data.id,
          state: "disconnected",
        },
      });
    }
  }, [type, data]);

  const acceptHandler = useCallback(() => {
    if (!data?.id) return;
    if (type === "case") {
      caseAnswer(data.id, data.self?.id);
    } else {
      const payload = {
        body: {
          participantId: data.self?.id,
          conversationId: data.id,
          state: "connected",
        },
      };
      Emitter.emit(
        type === "chat" ? API_CHAT_UPDATE : API_CALLBACK_UPDATE,
        payload
      );
    }
  }, [type, data]);

  useEffect(() => {
    if (
      data?.id &&
      (type !== "chat" || data?.self?.state === CHAT_STATES.RINGING)
    ) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [data?.id, data?.self?.state, type]);

  if (!isOpen) return null;

  const renderAttributeGroup = (group: any[], idx: number) => (
    <View key={`group-${idx}`} UNSAFE_className="call-incoming-attributes">
      <Flex direction="row" wrap gap="size-300">
        {group.map((attr, subIdx) => (
          <Flex
            key={`attr-${idx}-${subIdx}`}
            direction="column"
            UNSAFE_className="accept-content-attribute"
          >
            <Text UNSAFE_className="call-content-heading">
              {attr.attributeName.toLowerCase() === "notes"
                ? "Transfer Notes"
                : formatLabel(attr.attributeName)}
            </Text>
            <Text
              UNSAFE_className="call-content-descritpion"
              UNSAFE_style={{ wordBreak: "break-word" }}
            >
              {customerAttrs[attr.attributeName] || "-"}
            </Text>
          </Flex>
        ))}
      </Flex>
    </View>
  );

  const renderCaseExtras = () => {
    if (type !== "case") return null;
    const base64 = customerAttrs?.CASE_DESCRIPTION_BASE64 ?? "";
    let description = "";
    try {
      const decodedText = b64DecodeUnicode(base64);
      description =
        decodedText.length > 300
          ? decodedText.slice(0, 300) + "..."
          : decodedText;
    } catch {}
    return (
      <>
        <Flex direction="column" width="100%" marginTop={"32px"}>
          <Text UNSAFE_className="call-content-heading">Case Title</Text>
          <Text UNSAFE_className="call-content-descritpion">
            {customerAttrs?.CASE_TITLE ?? "-"}
          </Text>
        </Flex>
        {description && (
          <Flex direction="column" width="100%">
            <Text UNSAFE_className="call-content-heading">Description</Text>
            <Text UNSAFE_className="call-content-descritpion">
              {description}
            </Text>
          </Flex>
        )}
      </>
    );
  };

  return (
    <DialogTrigger type="modal" mobileType="modal" defaultOpen>
      <ActionButton isHidden isQuiet />
      {() => (
        <Dialog size="S" UNSAFE_className="call-incoming-dialog">
          <Heading level={4}>
            <Flex direction="row" gap="size-100" alignItems="center">
              <Text UNSAFE_className="call-heading-icon">{headingIcon}</Text>
              <Text UNSAFE_className="call-heading-text">{headingText}</Text>
            </Flex>
          </Heading>
          <Divider
            marginBottom="size-0"
            UNSAFE_className="call-incoming-divider"
          />
          <Content maxWidth="100vw" maxHeight={"50vh"}>
            <Flex direction="column" UNSAFE_className="accept-content">
              <Flex direction="row" width="100%">
                <Flex direction="column" width="45%">
                  <Flex>
                    <View UNSAFE_className="call-history-avatar">
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        UNSAFE_className="agent-avatar"
                        UNSAFE_style={{
                          backgroundColor: avatarColor as string,
                          borderRadius: "50%",
                        }}
                      >
                        <Text UNSAFE_className="call-monitor-agent-text">
                          {initials}
                        </Text>
                      </Flex>
                    </View>
                    <Flex direction="column" width="100%">
                      <Text UNSAFE_className="call-content-phone-heading">
                        {fullName}
                      </Text>
                      <Text UNSAFE_className="call-content-email-descritpion">
                        {emailOrId}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              {type === "case" && (
                <Flex direction="row" width="100%" marginTop="size-100">
                  <Flex direction="column" width="100%">
                    <Text UNSAFE_className="call-content-heading">Case ID</Text>
                    <Text UNSAFE_className="call-content-descritpion">
                      {customerAttrs?.CASEID ?? "-"}
                    </Text>
                  </Flex>
                </Flex>
              )}
              {groupedAttributes.map(renderAttributeGroup)}
              {renderCaseExtras()}
            </Flex>
          </Content>
          <ButtonGroup>
            <Button
              variant="negative"
              onPress={rejectHandler}
              UNSAFE_className="call-reject-btn"
              autoFocus={false}
            >
              {type !== "chat" && (
                <span className="reject-btn-icon">
                  <CallReject />
                </span>
              )}
              <Text UNSAFE_style={{ display: "contents" }}>Reject</Text>
            </Button>
            <Button
              variant="accent"
              onPress={acceptHandler}
              UNSAFE_className="call-accept-btn"
              autoFocus={false}
            >
              {type !== "chat" && (
                <span className="calling-btn-icon">
                  <CallAccept />
                </span>
              )}
              <Text UNSAFE_style={{ display: "contents" }}>Accept</Text>
            </Button>
          </ButtonGroup>
        </Dialog>
      )}
    </DialogTrigger>
  );
};

export default AcceptDialog;
